import React, { useState, useEffect } from 'react';

import { DataGrid, PageTitleWithToolTip } from '../Common';
import { StyledCellLink, Percentage, FractionPercentage, Weight } from '../Common/DataGrid/CellComponents';
import { text_commonCellProps, select_commonCellProps } from '../Common/DataGrid/utils';
import { getDateRangeFilterOperator, getRangeFilterOperator } from '../Common/DataGrid/FilterComponents';
import { fetchFilterValues } from '../../utils';
import Routes from '../../routes';
import { TranslateW } from '../../Utils/Languages/Translate';
import { CardHeader } from '@mui/material';

const dateRangeFilterOperator = getDateRangeFilterOperator({});
const rangeFilterOperator = getRangeFilterOperator({});

const columns = [
    {
        field: 'id',
        headerName: 'Service event ID',
        ...text_commonCellProps,
        renderCell: ({ row: { id } }) => (
            <StyledCellLink
                path={`/ServiceEvent/${id}`}
                text={id}
                from={window.location.pathname}
            />
        )
    },
    {
        field: 'workOrderId',
        headerName: 'Work order ID',
        ...text_commonCellProps,
        renderCell: ({ row: { workOrderId } }) => (
            <StyledCellLink
                path={`/WorkOrder/${workOrderId}`}
                text={workOrderId}
                from={window.location.pathname}
            />
        )
    },
    {
        field: 'workOrderNumber',
        headerName: 'Customer work #',
        ...select_commonCellProps
    },
    {
        field: 'type',
        headerName: 'Type',
        ...select_commonCellProps
    },
    {
        field: 'serviceDate',
        headerName: 'Service date',
        filterOperators: dateRangeFilterOperator,
    },
    {
        field: 'statusName',
        headerName: 'Status',
        ...text_commonCellProps
    },
    {
        field: 'servicerName',
        headerName: 'Service provider',
        ...text_commonCellProps
    },
    {
        field: 'primaryTechnicianName',
        headerName: 'Primary Technician',
        ...text_commonCellProps
    },
    {
        field: 'assetName',
        headerName: 'Asset name',
        ...text_commonCellProps
    },
    {
        field: 'materialType',
        headerName: 'Material type',
        ...select_commonCellProps
    },
    {
        field: 'netGasAdded',
        headerName: 'Net gas added',
        valueGetter: (value) => value,
        filterOperators: rangeFilterOperator,
        renderCell: ({ row: { netGasAdded } }) => <Weight value={netGasAdded} />
    },
    {
        field: 'leakRate',
        headerName: 'Per Occurrence Leak Rate',
        filterOperators: rangeFilterOperator,
        renderCell: ({ row: { leakRate } }) => <Percentage value={leakRate} />
    },
    {
        // AB#14852: EPA Rolling Average leak rate as of the SE date, per-page from the DWH.
        // Not sortable/filterable: the value is enriched onto the returned page only.
        field: 'epaRollingLeakRate',
        headerName: 'Leak Rate - EPA Rolling Avg (%)',
        initiallyVisible: false,
        sortable: false,
        filterable: false,
        renderCell: ({ row: { epaRollingLeakRate } }) => <FractionPercentage value={epaRollingLeakRate} />
    },
    {
        // AB#16788: true trailing-365-day rolling average as of the SE date, per-page from the DWH.
        // Not sortable/filterable: the value is enriched onto the returned page only.
        field: 'twelveMonthRollingLeakRate',
        headerName: '12-Month Average Leak Rate (%)',
        initiallyVisible: false,
        sortable: false,
        filterable: false,
        renderCell: ({ row: { twelveMonthRollingLeakRate } }) => <FractionPercentage value={twelveMonthRollingLeakRate} />
    },
    {
        // AB#16849: highest (peak) EPA leak rate over the trailing 12 months, from the
        // non-resetting DWH fact table (AB#15322 semantics) — same value on every row of the asset.
        field: 'highestEpaLeakRateLast12Months',
        headerName: 'Highest EPA Leak Rate - Last 12 Months',
        initiallyVisible: false,
        sortable: false,
        filterable: false,
        renderCell: ({ row: { highestEpaLeakRateLast12Months } }) => <FractionPercentage value={highestEpaLeakRateLast12Months} />
    },
    {
        // AB#16849: highest (peak) EPA leak rate since Jan 1 of the current calendar year.
        field: 'highestEpaLeakRateCalendarYear',
        headerName: 'Highest EPA Leak Rate - Calendar Year',
        initiallyVisible: false,
        sortable: false,
        filterable: false,
        renderCell: ({ row: { highestEpaLeakRateCalendarYear } }) => <FractionPercentage value={highestEpaLeakRateCalendarYear} />
    },
]

const ServiceEvents = ({ id }) => {
    const [filters, setFilters] = useState([]);

    const fetchFilters = async () => {
        const filters = await fetchFilterValues(Routes.Location.ServiceEventTableFilters(id));
        setFilters(filters);
    };

    useEffect(() => { fetchFilters() }, []);

    return (
        <>
            <DataGrid
                hideExportButton
                tableId={'location-service-event-table'}
                url={Routes.Location.ServiceEventTable(id)}
                filterOptions={filters}
                noResultText="Oops...It is empty here..."
                noResultText2="Create a new Service Event or change your search criteria."
                columns={columns}
                headerContent={
                    <CardHeader
                        title={
                            <PageTitleWithToolTip
                                pageTitle={TranslateW("serviceActivityView.service-events")}
                                toolTipText={TranslateW("ServiceEventsDescription")}
                            />
                        }
                        sx={{
                            "& .MuiCardHeader-action": { display: "flex" },
                            "& .MuiCardHeader-content": { maxWidth: "100%", textWrap: "nowrap" }
                        }}
                    />
                }
            />
        </>
    );
};

export default ServiceEvents;
